/**
 * Financial Reports Page
 * Generate IAS-compliant financial statements
 */

import React, { useState, useEffect } from 'react'
import { format } from 'date-fns'
import {
  FileText,
  Download,
  Calendar,
  TrendingUp,
  DollarSign,
  BarChart3,
  FileSpreadsheet,
  Printer,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { AdminUser } from '@/lib/authService'
import ReportService from '@/services/reportService'
import type {
  StatementOfFinancialPosition,
  StatementOfActivities,
  CashFlowStatement,
  BudgetVarianceReport,
} from '@/services/financeService'
import { formatCurrency } from '@/lib/currency'

interface FinancialReportsProps {
  user: AdminUser | null
}

type ReportType = 'financial_position' | 'activities' | 'cash_flow' | 'budget_variance'

export default function FinancialReports({ user }: FinancialReportsProps) {
  const [reportType, setReportType] = useState<ReportType>('financial_position')
  const [startDate, setStartDate] = useState(
    format(new Date(new Date().getFullYear(), 0, 1), 'yyyy-MM-dd')
  )
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'))
  const [fundId, setFundId] = useState<string>('all')
  const [loading, setLoading] = useState(false)

  // Report data
  const [financialPosition, setFinancialPosition] = useState<StatementOfFinancialPosition | null>(null)
  const [activities, setActivities] = useState<StatementOfActivities | null>(null)
  const [cashFlow, setCashFlow] = useState<CashFlowStatement | null>(null)
  const [budgetVariance, setBudgetVariance] = useState<BudgetVarianceReport | null>(null)

  useEffect(() => {
    loadReport()
  }, [reportType, startDate, endDate, fundId])

  async function loadReport() {
    if (!user?.churchId) return

    setLoading(true)
    try {
      switch (reportType) {
        case 'financial_position':
          const fp = await ReportService.getStatementOfFinancialPosition(
            user.churchId,
            endDate,
            fundId === 'all' ? undefined : fundId
          )
          setFinancialPosition(fp)
          break

        case 'activities':
          const act = await ReportService.getStatementOfActivities(
            user.churchId,
            startDate,
            endDate,
            fundId === 'all' ? undefined : fundId
          )
          setActivities(act)
          break

        case 'cash_flow':
          const cf = await ReportService.getCashFlowStatement(
            user.churchId,
            startDate,
            endDate
          )
          setCashFlow(cf)
          break

        case 'budget_variance':
          const bv = await ReportService.getBudgetVarianceReport(
            user.churchId,
            startDate,
            endDate
          )
          setBudgetVariance(bv)
          break
      }
    } catch (error) {
      console.error('Error loading report:', error)
    } finally {
      setLoading(false)
    }
  }

  async function handleExportPDF() {
    if (!user?.churchId) return

    try {
      let blob: Blob | null = null
      let filename = ''
      const churchName = 'FaithYoke Church' // TODO: Get actual church name from user data

      switch (reportType) {
        case 'financial_position':
          if (!financialPosition) return
          blob = await ReportService.exportReportToPDF(
            'statement_of_financial_position',
            financialPosition,
            { churchName, asOfDate: endDate }
          )
          filename = `Statement_of_Financial_Position_${format(new Date(endDate), 'yyyy-MM-dd')}.pdf`
          break

        case 'activities':
          if (!activities) return
          blob = await ReportService.exportReportToPDF(
            'statement_of_activities',
            activities,
            { churchName, startDate, endDate }
          )
          filename = `Statement_of_Activities_${format(new Date(startDate), 'yyyy-MM-dd')}_to_${format(new Date(endDate), 'yyyy-MM-dd')}.pdf`
          break

        default:
          alert('PDF export not yet available for this report type')
          return
      }

      if (blob) {
        // Create download link
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      }
    } catch (error) {
      console.error('Error exporting to PDF:', error)
      alert('Failed to export report to PDF. Please try again.')
    }
  }

  async function handleExportExcel() {
    if (!user?.churchId) return

    try {
      let blob: Blob | null = null
      let filename = ''
      const churchName = 'FaithYoke Church' // TODO: Get actual church name from user data

      switch (reportType) {
        case 'financial_position':
          if (!financialPosition) return
          blob = await ReportService.exportReportToExcel(
            'statement_of_financial_position',
            financialPosition,
            { churchName, asOfDate: endDate }
          )
          filename = `Statement_of_Financial_Position_${format(new Date(endDate), 'yyyy-MM-dd')}.xlsx`
          break

        case 'activities':
          if (!activities) return
          blob = await ReportService.exportReportToExcel(
            'statement_of_activities',
            activities,
            { churchName, startDate, endDate }
          )
          filename = `Statement_of_Activities_${format(new Date(startDate), 'yyyy-MM-dd')}_to_${format(new Date(endDate), 'yyyy-MM-dd')}.xlsx`
          break

        default:
          alert('Excel export not yet available for this report type')
          return
      }

      if (blob) {
        // Create download link
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      }
    } catch (error) {
      console.error('Error exporting to Excel:', error)
      alert('Failed to export report to Excel. Please try again.')
    }
  }

  function handlePrint() {
    window.print()
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold tracking-tight">Financial Reports</h1>
        <p className="text-muted-foreground">
          FASB ASC 958 compliant financial statements for non-profit organizations
        </p>
      </div>

      {/* Report Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Report Parameters</CardTitle>
          <CardDescription>Select report type and date range</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            {/* Report Type */}
            <div className="space-y-2">
              <Label htmlFor="reportType">Report Type</Label>
              <Select value={reportType} onValueChange={(value) => setReportType(value as ReportType)}>
                <SelectTrigger id="reportType">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="financial_position">
                    Statement of Financial Position
                  </SelectItem>
                  <SelectItem value="activities">
                    Statement of Activities
                  </SelectItem>
                  <SelectItem value="cash_flow">
                    Cash Flow Statement
                  </SelectItem>
                  <SelectItem value="budget_variance">
                    Budget vs Actual
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Start Date */}
            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            {/* End Date */}
            <div className="space-y-2">
              <Label htmlFor="endDate">End Date</Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            {/* Fund Filter */}
            <div className="space-y-2">
              <Label htmlFor="fund">Fund</Label>
              <Select value={fundId} onValueChange={setFundId}>
                <SelectTrigger id="fund">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Funds</SelectItem>
                  <SelectItem value="unrestricted">Unrestricted</SelectItem>
                  <SelectItem value="restricted">Restricted</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mt-4">
            <Button onClick={loadReport} disabled={loading}>
              <BarChart3 className="mr-2 h-4 w-4" />
              {loading ? 'Generating...' : 'Generate Report'}
            </Button>
            <Button variant="outline" onClick={handleExportPDF}>
              <Download className="mr-2 h-4 w-4" />
              Export PDF
            </Button>
            <Button variant="outline" onClick={handleExportExcel}>
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              Export Excel
            </Button>
            <Button variant="outline" onClick={handlePrint}>
              <Printer className="mr-2 h-4 w-4" />
              Print
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Report Display */}
      {loading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-muted-foreground">Generating report...</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Statement of Financial Position */}
          {reportType === 'financial_position' && financialPosition && (
            <Card>
              <CardHeader>
                <CardTitle>Statement of Financial Position (Non-Profit Balance Sheet)</CardTitle>
                <CardDescription>
                  As of {format(new Date(endDate), 'MMMM dd, yyyy')} • FASB ASC 958 Format: Assets = Liabilities + Net Assets
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Assets */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Assets</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Account</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(financialPosition.assets || []).map((account) => (
                        <TableRow key={account.account_id}>
                          <TableCell>{account.account_name}</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(account.balance, 'GHS')}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="font-bold">
                        <TableCell>Total Assets</TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(financialPosition.total_assets, 'GHS')}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                <Separator />

                {/* Liabilities */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Liabilities</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Account</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(financialPosition.liabilities || []).map((account) => (
                        <TableRow key={account.account_id}>
                          <TableCell>{account.account_name}</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(account.balance, 'GHS')}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="font-bold">
                        <TableCell>Total Liabilities</TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(financialPosition.total_liabilities, 'GHS')}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                <Separator />

                {/* Net Assets (Equity) */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Net Assets</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Account</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(financialPosition.net_assets || []).map((account) => (
                        <TableRow key={account.account_id}>
                          <TableCell>{account.account_name}</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(account.balance, 'GHS')}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="font-bold">
                        <TableCell>Total Net Assets</TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(financialPosition.total_net_assets, 'GHS')}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                <Separator />

                {/* Total (should balance) */}
                <div className="bg-muted p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold">Total Liabilities & Net Assets</span>
                    <span className="text-lg font-bold font-mono">
                      {formatCurrency(financialPosition.total_liabilities + financialPosition.total_net_assets, 'GHS')}
                    </span>
                  </div>
                  {Math.abs(financialPosition.total_assets - (financialPosition.total_liabilities + financialPosition.total_net_assets)) < 0.01 ? (
                    <Badge variant="default" className="mt-2">Balanced</Badge>
                  ) : (
                    <Badge variant="destructive" className="mt-2">Out of Balance</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Statement of Activities */}
          {reportType === 'activities' && activities && (
            <Card>
              <CardHeader>
                <CardTitle>Statement of Activities (Income Statement)</CardTitle>
                <CardDescription>
                  For the period {format(new Date(startDate), 'MMM dd, yyyy')} to {format(new Date(endDate), 'MMM dd, yyyy')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Revenue */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Revenue</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Account</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(activities.revenue || []).map((account) => (
                        <TableRow key={account.account_id}>
                          <TableCell>{account.account_name}</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(account.amount, 'GHS')}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="font-bold">
                        <TableCell>Total Revenue</TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(activities.total_revenue, 'GHS')}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                <Separator />

                {/* Expenses */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Expenses</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Account</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(activities.expenses || []).map((account) => (
                        <TableRow key={account.account_id}>
                          <TableCell>{account.account_name}</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(account.amount, 'GHS')}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="font-bold">
                        <TableCell>Total Expenses</TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(activities.total_expenses, 'GHS')}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                <Separator />

                {/* Net Income */}
                <div className="bg-muted p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold">Change in Net Assets</span>
                    <span className={`text-lg font-bold font-mono ${activities.net_income >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(activities.net_income, 'GHS')}
                    </span>
                  </div>
                  <Badge variant={activities.net_income >= 0 ? 'default' : 'destructive'} className="mt-2">
                    {activities.net_income >= 0 ? 'Surplus' : 'Deficit'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Cash Flow Statement */}
          {reportType === 'cash_flow' && cashFlow && (
            <Card>
              <CardHeader>
                <CardTitle>Statement of Cash Flows (IAS 7)</CardTitle>
                <CardDescription>
                  For the period {format(new Date(startDate), 'MMM dd, yyyy')} to {format(new Date(endDate), 'MMM dd, yyyy')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Operating Activities */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Cash Flows from Operating Activities</h3>
                  <Table>
                    <TableBody>
                      <TableRow key="contributions">
                        <TableCell>Contributions received</TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(cashFlow.operating_activities.contributions, 'GHS')}
                        </TableCell>
                      </TableRow>
                      <TableRow key="expenses">
                        <TableCell>Expenses paid</TableCell>
                        <TableCell className="text-right font-mono">
                          ({formatCurrency(Math.abs(cashFlow.operating_activities.expenses), 'GHS')})
                        </TableCell>
                      </TableRow>
                      <TableRow key="net-operating" className="font-bold">
                        <TableCell>Net Cash from Operating Activities</TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(cashFlow.operating_activities.net, 'GHS')}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                <Separator />

                {/* Investing Activities */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Cash Flows from Investing Activities</h3>
                  <Table>
                    <TableBody>
                      <TableRow key="asset-purchases">
                        <TableCell>Asset purchases</TableCell>
                        <TableCell className="text-right font-mono">
                          ({formatCurrency(Math.abs(cashFlow.investing_activities.asset_purchases), 'GHS')})
                        </TableCell>
                      </TableRow>
                      <TableRow key="asset-sales">
                        <TableCell>Asset sales</TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(cashFlow.investing_activities.asset_sales, 'GHS')}
                        </TableCell>
                      </TableRow>
                      <TableRow key="net-investing" className="font-bold">
                        <TableCell>Net Cash from Investing Activities</TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(cashFlow.investing_activities.net, 'GHS')}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                <Separator />

                {/* Financing Activities */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Cash Flows from Financing Activities</h3>
                  <Table>
                    <TableBody>
                      <TableRow key="borrowings">
                        <TableCell>Borrowings received</TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(cashFlow.financing_activities.borrowings, 'GHS')}
                        </TableCell>
                      </TableRow>
                      <TableRow key="repayments">
                        <TableCell>Loan repayments</TableCell>
                        <TableCell className="text-right font-mono">
                          ({formatCurrency(Math.abs(cashFlow.financing_activities.repayments), 'GHS')})
                        </TableCell>
                      </TableRow>
                      <TableRow key="net-financing" className="font-bold">
                        <TableCell>Net Cash from Financing Activities</TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(cashFlow.financing_activities.net, 'GHS')}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                <Separator />

                {/* Net Change in Cash */}
                <div className="bg-muted p-4 rounded-lg space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Net Increase/(Decrease) in Cash</span>
                    <span className="font-mono">
                      {formatCurrency(cashFlow.net_change, 'GHS')}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Cash at Beginning of Period</span>
                    <span className="font-mono">
                      {formatCurrency(cashFlow.beginning_balance, 'GHS')}
                    </span>
                  </div>
                  <Separator />
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold">Cash at End of Period</span>
                    <span className="text-lg font-bold font-mono">
                      {formatCurrency(cashFlow.ending_balance, 'GHS')}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Budget Variance Report */}
          {reportType === 'budget_variance' && budgetVariance && (
            <Card>
              <CardHeader>
                <CardTitle>Budget vs Actual Variance Report</CardTitle>
                <CardDescription>
                  For the period {format(new Date(startDate), 'MMM dd, yyyy')} to {format(new Date(endDate), 'MMM dd, yyyy')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Account</TableHead>
                      <TableHead className="text-right">Budget</TableHead>
                      <TableHead className="text-right">Actual</TableHead>
                      <TableHead className="text-right">Variance</TableHead>
                      <TableHead className="text-right">% Variance</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(budgetVariance.line_items || []).map((item) => (
                      <TableRow key={item.account_id}>
                        <TableCell>{item.account_name}</TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(item.budgeted, 'GHS')}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(item.actual, 'GHS')}
                        </TableCell>
                        <TableCell className={`text-right font-mono ${item.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(item.variance, 'GHS')}
                        </TableCell>
                        <TableCell className={`text-right ${Math.abs(item.variance_percent) > 10 ? 'text-orange-600 font-semibold' : ''}`}>
                          {item.variance_percent.toFixed(1)}%
                        </TableCell>
                        <TableCell>
                          {Math.abs(item.variance_percent) > 10 ? (
                            <Badge variant="destructive">Over Budget</Badge>
                          ) : (
                            <Badge variant="default">On Track</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
